import { useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { Error } from "../../../../../../../models/error";
import { ItemTypes } from "../../../../../../../models/item.types";
import { Filters, Searches, Sorts, Sort } from "../../../../../../../models/shipeezi.table";
import ItemTypesService from "../../../../../../../services/item.types.service";
import { useAppState } from "../../../../../../../context/StateContext";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";

const useActions = () => {
  const { currentUser } = useAppState();

  const [itemTypeList, setItemTypeList] = useState<ItemTypes[]>([]);
  const [hasErrors, setHasErrors] = useState<boolean>();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();

  const { refetch, isFetching } = useQuery(
    ["item.types.list", page, pageSize, filters, searches, sorting],
    () =>
      ItemTypesService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        sortBy: sorting
          ? JSON.stringify([...sorting, ["name", "asc"]])
          : JSON.stringify([["name", "asc"]]),
        include: JSON.stringify([{ association: "rooms", attributes: ["id", "name", "code"] }]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setItemTypeList(response.data);
      },
    }
  );

  const { refetch: refetchItemTypesTotal } = useQuery(
    ["item.types.total", filters, searches],
    () =>
      ItemTypesService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
        else setTotal(0);
      },
    }
  );

  const { mutate: updateItemType, isLoading: isUpdating } = useMutation(
    ["item.types.update"],
    ({ id, itemType }: { id: number; itemType: ItemTypes }) =>
      ItemTypesService.update(id, itemType),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        refetchItemTypesTotal();
      },
      onError: (error: AxiosError<Error>) => {
        setHasErrors(true);
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    }
  );

  const { mutate: createItemType, isLoading: isCreating } = useMutation(
    ["item.types.create"],
    (itemType: ItemTypes) => ItemTypesService.create(itemType),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        refetchItemTypesTotal();
      },
      onError: (error: AxiosError<Error>) => {
        setHasErrors(true);
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    }
  );

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  return {
    page,
    pageSize,
    total,
    hasErrors,
    itemTypeList,
    isLoading: isFetching || isCreating || isUpdating,
    setHasErrors,
    setPageSize,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createItemType,
    updateItemType,
  };
};

export default useActions;
